import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import axios from "axios";

/**
 * AutoTranslate — site-wide AI translation layer.
 *
 * When the active language is not English, this walks the rendered DOM and
 * translates visible text (and key attributes) into the target language via the
 * backend AI translation service (/api/i18n/translate, cached in MongoDB).
 *
 * Pages already wired with react-i18next (Navbar, GivingPage, EventPage…) opt
 * out via `data-no-translate` on their root so their polished strings win.
 *
 * Language switches reload the page (see LanguageToggle), so this only ever has
 * to translate EN -> target on load; it never restores text.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SKIP_TAGS = new Set([
    "SCRIPT", "STYLE", "NOSCRIPT", "IFRAME", "SVG", "PATH", "CANVAS",
    "CODE", "PRE", "TEXTAREA", "INPUT", "SELECT",
]);
const ATTRS = ["placeholder", "title", "aria-label", "alt"];
const cache = new Map(); // normalized source -> translated

const hasLetters = (s) => /[A-Za-zÀ-ÿ]/.test(s);
const norm = (s) => s.replace(/\s+/g, " ").trim();

export default function AutoTranslate() {
    const { i18n } = useTranslation();
    const loc = useLocation();
    const processed = useRef(new WeakSet());
    const timer = useRef(null);
    const headTimer = useRef(null);

    useEffect(() => {
        const lang = (i18n.language || "en").split("-")[0];
        if (lang === "en") return; // English is the source — nothing to do.

        let stopped = false;

        const inSkip = (node) => {
            let el = node.nodeType === 3 ? node.parentElement : node;
            while (el) {
                if (SKIP_TAGS.has(el.tagName)) return true;
                if (el.hasAttribute && el.hasAttribute("data-no-translate")) return true;
                el = el.parentElement;
            }
            return false;
        };

        const collect = () => {
            const items = []; // { src, apply }
            const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
                acceptNode(n) {
                    if (processed.current.has(n)) return NodeFilter.FILTER_REJECT;
                    const v = n.nodeValue;
                    if (!v || !hasLetters(v) || norm(v).length < 2) return NodeFilter.FILTER_REJECT;
                    if (inSkip(n)) return NodeFilter.FILTER_REJECT;
                    return NodeFilter.FILTER_ACCEPT;
                },
            });
            let walkNode;
            while ((walkNode = walker.nextNode())) {
                const node = walkNode; // block-scoped capture for the closure below
                const original = node.nodeValue;
                const src = norm(original);
                processed.current.add(node);
                const lead = original.match(/^\s*/)[0];
                const trail = original.match(/\s*$/)[0];
                items.push({ src, apply: (tr) => {
                    if (node) node.nodeValue = lead + tr + trail;
                } });
            }

            const sel = ATTRS.map((a) => `[${a}]`).join(",");
            document.body.querySelectorAll(sel).forEach((el) => {
                if (inSkip(el)) return;
                ATTRS.forEach((a) => {
                    const val = el.getAttribute(a);
                    if (!val || !hasLetters(val) || norm(val).length < 2) return;
                    el.__cf_tr = el.__cf_tr || {};
                    if (el.__cf_tr[a] === val) return; // already handled
                    items.push({
                        src: norm(val),
                        apply: (tr) => { el.setAttribute(a, tr); el.__cf_tr[a] = tr; },
                    });
                });
            });
            return items;
        };

        const run = async () => {
            if (stopped) return;
            const items = collect();
            if (!items.length) return;

            const misses = [];
            for (const it of items) {
                if (cache.has(it.src)) it.apply(cache.get(it.src));
                else misses.push(it);
            }
            if (!misses.length) return;

            const uniq = [...new Set(misses.map((m) => m.src))].slice(0, 600);
            const CHUNK = 25; // small batches finish well under the gateway timeout
            for (let i = 0; i < uniq.length; i += CHUNK) {
                if (stopped) return;
                const chunk = uniq.slice(i, i + CHUNK);
                try {
                    const { data } = await axios.post(`${API}/i18n/translate`, {
                        texts: chunk, target_lang: lang,
                    }, { timeout: 45000 });
                    const map = data.translations || {};
                    for (const k of chunk) cache.set(k, map[k] || k);
                } catch {
                    for (const k of chunk) cache.set(k, k); // graceful: keep source
                }
                for (const it of misses) {
                    if (cache.has(it.src)) it.apply(cache.get(it.src));
                }
            }
        };

        // Translate <head> SEO so the browser tab, search snippets and social
        // cards are localized too — works regardless of whether a page uses
        // SEOHead or react-helmet. A guard set prevents observer feedback loops.
        const headDone = new Set();
        const HEAD_TARGETS = [
            { sel: "title", attr: null },
            { sel: 'meta[name="description"]', attr: "content" },
            { sel: 'meta[property="og:title"]', attr: "content" },
            { sel: 'meta[property="og:description"]', attr: "content" },
            { sel: 'meta[name="twitter:title"]', attr: "content" },
            { sel: 'meta[name="twitter:description"]', attr: "content" },
        ];
        const readHead = (t) => {
            const el = document.head.querySelector(t.sel);
            if (!el) return null;
            const cur = t.attr ? el.getAttribute(t.attr) : el.textContent;
            return { el, cur };
        };
        const runHead = async () => {
            if (stopped) return;
            const jobs = [];
            for (const t of HEAD_TARGETS) {
                const r = readHead(t);
                if (!r || !r.cur || !hasLetters(r.cur)) continue;
                const src = norm(r.cur);
                if (headDone.has(src)) continue; // already a translation we produced
                jobs.push({ ...t, el: r.el, src });
            }
            if (!jobs.length) return;
            const uniq = [...new Set(jobs.map((j) => j.src))].filter((s) => !cache.has(s));
            for (let i = 0; i < uniq.length; i += 25) {
                if (stopped) return;
                const chunk = uniq.slice(i, i + 25);
                try {
                    const { data } = await axios.post(`${API}/i18n/translate`, {
                        texts: chunk, target_lang: lang,
                    }, { timeout: 45000 });
                    const map = data.translations || {};
                    for (const k of chunk) cache.set(k, map[k] || k);
                } catch {
                    for (const k of chunk) cache.set(k, k);
                }
            }
            for (const j of jobs) {
                const tr = cache.get(j.src);
                if (!tr || tr === j.src) continue;
                if (j.attr) j.el.setAttribute(j.attr, tr); else j.el.textContent = tr;
                headDone.add(norm(tr));
            }
        };

        const schedule = () => {
            if (timer.current) clearTimeout(timer.current);
            timer.current = setTimeout(() => { run(); runHead(); }, 300);
        };

        schedule();
        // Re-translate content added by SPA navigation, modals, lazy data, etc.
        const mo = new MutationObserver(schedule);
        mo.observe(document.body, { childList: true, subtree: true });
        // Watch the <head> too (SEOHead / Helmet rewrite the title on navigation).
        const headMo = new MutationObserver(() => {
            if (headTimer.current) clearTimeout(headTimer.current);
            headTimer.current = setTimeout(runHead, 200);
        });
        headMo.observe(document.head, { childList: true, subtree: true, characterData: true });

        return () => {
            stopped = true;
            mo.disconnect();
            headMo.disconnect();
            if (timer.current) clearTimeout(timer.current);
            if (headTimer.current) clearTimeout(headTimer.current);
        };
    }, [i18n.language, loc.pathname]);

    return null;
}
